/**
 * SIMULATION ONLY — stands in for a remote camera track until the LiveKit SDK
 * is wired. It exists so the UI genuinely exercises attach/detach, pause-on-
 * hidden and subscribe-only-visible instead of faking those code paths.
 *
 * It is deliberately cheap: 320x180, 5 fps, and the draw loop runs ONLY while
 * at least one <video> is attached.
 */
export interface SimulatedVideoSource {
  attach(el: HTMLVideoElement): () => void;
  stop(): void;
}

const WIDTH = 320;
const HEIGHT = 180;
const FPS = 5;

export function createSimulatedVideoSource(label: string, seed: number): SimulatedVideoSource {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');
  const stream = canvas.captureStream(FPS);
  const hue = (seed * 47) % 360;

  let attached = 0;
  let timer: number | null = null;
  let frame = 0;

  const draw = (): void => {
    if (!ctx) return;
    frame += 1;
    const drift = Math.sin(frame / 18) * 8;
    const gradient = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT);
    gradient.addColorStop(0, `hsl(${hue} 42% ${26 + drift * 0.2}%)`);
    gradient.addColorStop(1, `hsl(${(hue + 40) % 360} 38% 16%)`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    ctx.beginPath();
    ctx.arc(WIDTH / 2, HEIGHT / 2 + 18 + drift, 44, 0, Math.PI * 2);
    ctx.fillStyle = `hsl(${hue} 30% ${44 + drift * 0.3}%)`;
    ctx.fill();

    ctx.font = '600 15px Inter, system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.textAlign = 'center';
    ctx.fillText(label, WIDTH / 2, HEIGHT - 22);
  };

  const start = (): void => {
    if (timer !== null) return;
    draw();
    timer = window.setInterval(draw, Math.round(1000 / FPS));
  };

  const pause = (): void => {
    if (timer === null) return;
    window.clearInterval(timer);
    timer = null;
  };

  return {
    attach(el) {
      attached += 1;
      el.srcObject = stream;
      void el.play().catch(() => undefined);
      start();
      return () => {
        attached -= 1;
        if (el.srcObject === stream) el.srcObject = null;
        if (attached <= 0) pause();
      };
    },
    stop() {
      pause();
      for (const track of stream.getTracks()) track.stop();
    },
  };
}
